/**
 * Modal to create a prompt template. Edit happens in the full-page
 * editor (Jinja blobs are big), so this only collects the metadata
 * needed to satisfy the (name, scope, key) uniqueness constraint and
 * a starter `template_jinja`.
 */

import { useEffect, useState } from "react";

import { Button, Modal, Select } from "@omnitok/ui";

import { HttpError } from "@/lib/api";
import { draftsApi } from "@/lib/builder-api";
import {
  promptsApi,
  type PromptScope,
  type PromptTemplate,
} from "@/lib/prompts-api";
import { useT } from "@/i18n";
import type { ReportDraft } from "@/types";

interface PromptCreateModalProps {
  onClose: () => void;
  onCreated: (created: PromptTemplate) => void;
}

export function PromptCreateModal({
  onClose,
  onCreated,
}: PromptCreateModalProps): JSX.Element {
  const { t } = useT();
  const [name, setName] = useState("");
  const [scope, setScope] = useState<PromptScope>("global");
  const [reportDraftId, setReportDraftId] = useState<string>("");
  const [clientKey, setClientKey] = useState("");
  const [templateJinja, setTemplateJinja] = useState("");
  const [drafts, setDrafts] = useState<ReportDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void draftsApi
      .list()
      .then((res) => setDrafts(res.drafts))
      .catch((err: unknown) =>
        setError(err instanceof HttpError ? err.message : String(err)),
      );
  }, []);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await promptsApi.create({
        name: name.trim(),
        scope,
        report_draft_id: scope === "report" ? reportDraftId : null,
        client_key: scope === "client" ? clientKey.trim() : null,
        template_jinja: templateJinja || "{# template body #}",
        is_active: true,
      });
      onCreated(created);
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("admin.prompts.create.title")}>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-700">
            {t("admin.prompts.create.nameLabel")}
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="level1_system"
            className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono text-xs"
          />
          <p className="mt-1 text-xs text-neutral-500">
            {t("admin.prompts.create.nameHint")}
          </p>
        </div>

        <div>
          <Select
            label={t("admin.prompts.create.scopeLabel")}
            options={[
              { value: "global", label: t("admin.prompts.scope.global") },
              { value: "report", label: t("admin.prompts.scope.report") },
              { value: "client", label: t("admin.prompts.scope.client") },
            ]}
            value={scope}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setScope(e.target.value as PromptScope)
            }
            fullWidth
          />
        </div>

        {scope === "report" ? (
          <div>
            <Select
              label={t("admin.prompts.create.draftLabel")}
              options={[
                { value: "", label: `— ${t("admin.common.select")} —` },
                ...drafts.map((d) => ({ value: d.id, label: d.key })),
              ]}
              value={reportDraftId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setReportDraftId(e.target.value)
              }
              required
              fullWidth
            />
          </div>
        ) : null}

        {scope === "client" ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              {t("admin.prompts.create.clientLabel")}
            </label>
            <input
              value={clientKey}
              onChange={(e) => setClientKey(e.target.value)}
              required
              className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono text-xs"
            />
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-700">
            {t("admin.prompts.create.templateLabel")}
          </label>
          <textarea
            rows={6}
            value={templateJinja}
            onChange={(e) => setTemplateJinja(e.target.value)}
            placeholder="{# template body — full editor opens after create #}"
            className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono text-xs"
          />
        </div>

        {error ? <p className="text-xs text-error-dark">{error}</p> : null}

        <div className="flex justify-end gap-2">
          <Button
            ghost
            onClick={onClose}
            disabled={submitting}
          >
            {t("admin.common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            loading={submitting}
          >
            {t("admin.prompts.create.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
